<template>
  <div>
    <!-- 单图上传 -->
    <template v-if="!props.multiple">
      <div v-if="props.modelValue" :style="imgStyle" class="img-item">
        <el-image :src="(props.modelValue as string)" fit="contain" style="width: 100%; height: 100%" />
        <div class="del-icon">
          <svg-icon class="icon" height="30" name="close" width="30" @click.stop="handleDelImg" />
        </div>
      </div>
      <div v-else>
        <div v-loading="loading" :style="imgStyle" class="upload-box cursor-pointer" @click="() => handleUpload()">
          <slot name="icon">
            <el-icon :size="20" style="color: #949ca2">
              <Picture />
            </el-icon>
          </slot>
          <span v-if="props.title" class="title">{{ props.title }}</span>
        </div>
      </div>
    </template>

    <!-- 多图上传 -->
    <div v-else class="flex items-center gap-3 flex-wrap">
      <div v-for="(item, index) in props.modelValue" :key="item" :style="imgStyle" class="img-item">
        <el-image v-loading="loadingMap[index]" :src="item" fit="contain" style="width: 100%; height: 100%" />
        <div class="del-icon">
          <svg-icon class="icon" height="30" name="close" width="30" @click.stop="handleDelImg(index)" />
        </div>
      </div>
      <div v-loading="loading">
        <div v-if="props.modelValue.length < props.max" :style="imgStyle" class="upload-box cursor-pointer" @click="() => handleUpload()">
          <slot name="icon">
            <el-icon :size="20" style="color: #949ca2">
              <Picture />
            </el-icon>
          </slot>
          <span v-if="props.title" class="title">{{ props.title }}</span>
        </div>
      </div>
    </div>

    <p class="desc" v-if="props.desc">{{ props.desc }}</p>

    <!--上传-->
    <el-upload
      ref="uploadRef"
      :action="proxyUrl + '/api/v1/sys/uploadImg'"
      :before-upload="beforeImageUpload"
      :headers="{ Authorization: 'Bearer ' + token }"
      :on-error="onError"
      :on-success="handleSuccess"
      :show-file-list="false"
      accept=".jpeg,.png,.jpg,.bmp"
      name="img"
      class="hidden"></el-upload>
  </div>
</template>

<script lang="ts" setup>
import {computed, reactive, ref} from 'vue'
import type {UploadInstance} from 'element-plus'
import {ElMessage} from 'element-plus'

interface IProps {
  modelValue: string | string[]
  title?: string
  multiple?: boolean
  max?: number
  width?: number
  height?: number
  desc?: string
  limit?: number
}

const props = withDefaults(defineProps<IProps>(), {
  multiple: false,
  max: 5,
  width: 80,
  height: 80,
  limit: 2
})
const emits = defineEmits(['update:modelValue', 'change'])

// 状态管理
const loading = ref(false)
const loadingMap = reactive<Record<number, boolean>>({})
const uploadIdx = ref<number | null>(null)

// 计算属性
const imgStyle = computed(() => ({
  width: props.width + 'px',
  height: props.height + 'px'
}))

// 上传相关方法
const handleUpload = (idx?: number) => {
  uploadIdx.value = idx ?? null
  uploadRef.value.$el.querySelector('input').click()
}

//删除图片
const handleDelImg = (idx?: number) => {
  if (props.multiple) {
    const newValue = (props.modelValue as string[]).filter((_: any, i: number) => i !== idx)
    emits('update:modelValue', newValue)
    emits('change')
  } else {
    emits('update:modelValue', '')
    emits('change', '')
  }

}

const uploadRef = ref<UploadInstance>()

// 上传图片
const token = localStorage.getItem('token')
let proxyUrl = import.meta.env.VITE_APP_PROXY_PREFIX
const beforeImageUpload = (rawFile) => {
  const whiteList = ['image/jpg', 'image/jpeg', 'image/png', 'image/gift']
  const fileSizeInMB = rawFile.size / 1024 / 1024

  if (whiteList.indexOf(rawFile.type) == -1) {
    ElMessage.warning('图片类型不符合要求！')
    return false
  } else if (fileSizeInMB > props.limit) {
    ElMessage.warning(`图片大小不能超过${props.limit}M！`)
    return false
  }
  if (uploadIdx.value !== null) {
    loadingMap[uploadIdx.value] = true
  } else {
    loading.value = true
  }
  return true
}
const onError = (err: any) => {
  if (uploadIdx.value !== null) {
    loadingMap[uploadIdx.value] = false
  } else {
    loading.value = false
  }
  ElMessage.warning('上传失败')
}

const handleSuccess = (response, file, fileList) => {
  const { code, data, message } = response
  if (code === 200) {
    if (props.multiple) {
      const newValue =
        uploadIdx.value !== null
          ? (props.modelValue as string[]).map((url: string, i: number) => (i === uploadIdx.value ? data.imgUrl : url))
          : [...props.modelValue, data.imgUrl]
      emits('update:modelValue', newValue)

    } else {
      emits('update:modelValue', data.imgUrl)
    }
    emits('change', file)
  } else {
    ElMessage.warning(message)
  }
  if (uploadIdx.value !== null) {
    loadingMap[uploadIdx.value] = false
  } else {
    loading.value = false
  }
}
</script>

<style lang="scss" scoped>
.upload-box {
  border-radius: 8px;
  background: #fff;
  box-sizing: border-box;
  border: 1px solid #e9ebf0;
  font-size: 14px;
  line-height: 22px;
  color: #656c7c;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  gap: 4px;

  .primary-color {
    color: rgb(0, 178, 157);
  }
}

.img-item {
  position: relative;
  border-radius: 8px;
  background: #ffffff;
  box-sizing: border-box;
  border: 1px solid #e9ebf0;
  overflow: hidden;

  .del-icon {
    position: absolute;
    right: 5px;
    top: 5px;
    cursor: pointer;
    color: #f53f3f;
  }
}

.title {
  font-size: 13px;
  color: #949ca2;
}

.desc {
  font-size: 13px;
  line-height: 22px;
  color: #949ca2;
  margin-top: 6px;
}
</style>
